// Shared CSV helpers: safe RFC-4180 escaping + simple parser for portfolio import.

export function csvEscape(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

export function buildCsv(headers, rows) {
  const line = (r) => r.map(csvEscape).join(",");
  return [line(headers), ...rows.map(line)].join("\n");
}

export function downloadBlob(content, filename, mime = "text/csv;charset=utf-8;") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Minimal RFC-4180 CSV parser (handles quoted fields, embedded quotes, commas, newlines).
export function parseCsv(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else { field += ch; }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") { cur.push(field); field = ""; }
      else if (ch === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; }
      else if (ch === "\r") { /* skip */ }
      else field += ch;
    }
  }
  if (field !== "" || cur.length) { cur.push(field); rows.push(cur); }
  return rows;
}

// Build the flattened portfolio row list for CSV export.
export const PORTFOLIO_CSV_HEADERS = [
  "portfolio_name", "ticker", "weight_pct", "initial_investment",
  "start_date", "end_date", "drip", "description", "created_at", "portfolio_id",
];

export function portfolioToCsvRows(p) {
  return (p.holdings || []).map((h) => [
    p.name, h.ticker, h.weight, p.initial_investment,
    p.start_date, p.end_date, p.drip ? "yes" : "no",
    p.description || "", p.created_at || "", p.id || "",
  ]);
}

// Group parsed CSV rows back into portfolio objects for /api/portfolios POST.
export function csvRowsToPortfolios(rows) {
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (name) => header.indexOf(name);
  const iName = idx("portfolio_name");
  const iTicker = idx("ticker");
  const iWeight = idx("weight_pct");
  const iInit = idx("initial_investment");
  const iStart = idx("start_date");
  const iEnd = idx("end_date");
  const iDrip = idx("drip");
  const iDesc = idx("description");
  if ([iName, iTicker, iWeight, iInit, iStart, iEnd].some((i) => i < 0)) {
    throw new Error("Missing required columns");
  }
  const byName = new Map();
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row.length || (row.length === 1 && row[0] === "")) continue;
    const name = (row[iName] || "").trim();
    if (!name) continue;
    if (!byName.has(name)) {
      byName.set(name, {
        name,
        description: iDesc >= 0 ? (row[iDesc] || "") : "",
        holdings: [],
        initial_investment: Number(row[iInit]) || 10000,
        start_date: row[iStart] || "2020-01-01",
        end_date: row[iEnd] || new Date().toISOString().slice(0, 10),
        drip: iDrip >= 0 ? ["yes", "true", "1", "y"].includes(String(row[iDrip]).toLowerCase()) : true,
      });
    }
    const p = byName.get(name);
    const ticker = (row[iTicker] || "").trim().toUpperCase();
    const weight = Number(row[iWeight]);
    if (ticker && !isNaN(weight)) p.holdings.push({ ticker, weight });
  }
  return [...byName.values()].filter((p) => p.holdings.length > 0);
}
